import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "antd";
import { InfoCircleTwoTone } from "@ant-design/icons";

// Default icon instance to avoid creating new elements on each render
const DEFAULT_ICON = <InfoCircleTwoTone />;

/**
 * Tooltip icon component for inline contextual help.
 *
 * Renders an icon with a tooltip, typically used for displaying contextual help or information.
 * Automatically detects two-tone Ant Design icons for proper color handling.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.title - The tooltip text to display (required).
 * @param {React.ReactElement} [props.icon=InfoCircleTwoTone] - The icon to render.
 * @param {string} [props.color] - Optional color for the icon (uses twoToneColor for two-tone icons).
 * @param {string|number} [props.size="inherit"] - The icon size (CSS value or number in px).
 * @param {string} [props.margin="0 0 0 6px"] - CSS margin shorthand for the icon.
 * @param {Object} [props.style] - Additional styles for the icon element.
 * @returns {JSX.Element} The tooltip-wrapped icon.
 */
const TooltipIcon = ({
  title,
  icon = DEFAULT_ICON,
  color,
  size = "inherit",
  margin = "0 0 0 6px",
  style,
}) => {
  const iconStyle = useMemo(() => {
    const fontSize = typeof size === "number" ? `${size}px` : size;
    return {
      fontSize,
      margin,
    };
  }, [size, margin]);

  const isTwoToneIcon = useMemo(() => {
    return icon.type?.displayName?.includes("TwoTone") || false;
  }, [icon.type]);

  const baseStyle = useMemo(
    () => ({
      ...icon.props?.style,
      ...style,
      ...iconStyle,
    }),
    [icon.props?.style, style, iconStyle]
  );

  const iconElement = React.cloneElement(icon, {
    style: color && !isTwoToneIcon ? { ...baseStyle, color } : baseStyle,
    ...(color && isTwoToneIcon && { twoToneColor: color }),
  });

  return <Tooltip title={title}>{iconElement}</Tooltip>;
};

TooltipIcon.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.element,
  color: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  margin: PropTypes.string,
  style: PropTypes.object,
};

export default TooltipIcon;
